import { QuestionMarkCircleIcon } from '@heroicons/react/16/solid'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { PoolsQuestion } from '../../../Services/OrderWizard/questions.ts'
import type { PoolContents } from '../../../Services/OrderWizard/pools.ts'
import { poolSizes } from '../../../Services/OrderWizard/pools.ts'
import { keptAnswer, suggestedAnswer } from '../../../Services/OrderWizard/poolBoard.ts'
import { questionTarget } from '../../../Services/OrderWizard/issues.ts'
import { poolCopy } from '../../../Services/OrderWizard/wizardSettings.ts'
import { useAnswer } from './useAnswer.ts'
import { InfoDisclosure } from './InfoDisclosure.tsx'
import { Callout, InlineWarning } from './Primitives.tsx'

export const METHOD_TEXT = {
  wells: 'the well positions in the file names',
  'sample-numbers': 'the Illumina sample numbers (_S…_) per run',
  sheet: "the sample sheet's pool column"
}

const SuggestionOffer = ({ draft, question, onEdit }: { draft: DraftState; question: PoolsQuestion; onEdit: () => void }) => {
  const answer = useAnswer(draft.id)
  const suggestion = question.suggestion
  if (!suggestion) {
    return null
  }
  const accept = () => answer(suggestedAnswer(suggestion))
  return (
    <div className="space-y-1 rounded border border-slate-200 bg-white p-2 text-sm" data-testid="wizard-pools-suggestion">
      <p className="text-slate-800">
        {suggestion.method === 'sheet' ? "The sample sheet's pool column groups" : 'The file names suggest'} {suggestion.count} groups: use
        them as pools?
      </p>
      {suggestion.reason && (
        <InlineWarning layout="inline" data-testid="wizard-pools-sheet-reason">
          Not applied on its own: {suggestion.reason}
        </InlineWarning>
      )}
      <p className="text-xs text-slate-600">
        From {METHOD_TEXT[suggestion.method]}: {poolSizes(suggestion.pools).join(' + ')} items.
      </p>
      {suggestion.withoutControl.length > 0 && (
        <InlineWarning layout="inline" data-testid="wizard-pools-low-confidence">
          Low confidence: no negative control in group {suggestion.withoutControl.map((group) => group + 1).join(', ')}, so the grouping may
          be off.
        </InlineWarning>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn btn-xs" data-testid="wizard-pools-accept" onClick={accept}>
          Use them as pools
        </button>
        <button type="button" className="btn btn-xs btn-ghost" data-testid="wizard-pools-edit-suggestion" onClick={onEdit}>
          Edit them first
        </button>
      </div>
    </div>
  )
}

export const PoolIntro = () => (
  <InfoDisclosure label="What is a pool?">
    <p data-testid="wizard-pools-intro">{poolCopy().intro}</p>
  </InfoDisclosure>
)

/** The pools as they are, kept with one click; the file names' suggestion taken as it is or edited first. */
export const PoolsQuestionCard = ({
  draft,
  question,
  contents,
  pending,
  keepLeads,
  onEditSuggestion
}: {
  draft: DraftState
  question: PoolsQuestion
  contents: PoolContents[]
  /** Whether the board holds unapplied changes, which its own bar applies. */
  pending: boolean
  /** Whether "Keep" is the screen's next action; not while another question on the step is open. */
  keepLeads: boolean
  onEditSuggestion: () => void
}) => {
  const answer = useAnswer(draft.id)
  const several = contents.length > 1
  const keep = () => answer(keptAnswer(draft))
  return (
    <Callout
      id={questionTarget('pools')}
      className="space-y-2 rounded-lg bg-white p-3 text-sm transition-shadow"
      data-testid="wizard-pools-question"
    >
      <p className="flex items-center gap-1.5 font-medium text-slate-900">
        <QuestionMarkCircleIcon className="h-4 w-4 text-amber-500" /> Do all samples share these negative controls?
      </p>
      <PoolIntro />
      <div
        className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-300 bg-slate-50 p-2"
        data-testid="wizard-pools-default"
      >
        <span className="font-medium text-slate-800">{several ? `As now, ${contents.length} pools` : 'One pool'}</span>
        {!pending && (
          <button type="button" className={`btn btn-sm ${keepLeads ? 'btn-primary' : ''}`} data-testid="wizard-pools-keep" onClick={keep}>
            {several ? 'Keep these pools' : 'Keep one pool'}
          </button>
        )}
      </div>
      {!pending && <SuggestionOffer draft={draft} question={question} onEdit={onEditSuggestion} />}
      {!pending && (
        <p className="text-slate-600" data-testid="wizard-pools-how">
          {several ? 'To change them, drag items between the pools below.' : 'If not, drag items onto “New pool” below.'}
        </p>
      )}
    </Callout>
  )
}
